import React from 'react';
import { 
  Container, 
  Typography, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const Home = ({ camps, weeks, campTypes, loading, error, fetchCamps, getAvailabilityBadge, isWeekAvailable }) => {
  const navigate = useNavigate();

  if (loading) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Loading camps...
      </Typography>
      <CircularProgress />
    </Container>
  );

  if (error) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" color="error" sx={{ mb: 2 }}>
        {error}
      </Typography>
      <Button 
        variant="contained" 
        onClick={() => fetchCamps()}
        sx={{ mt: 2 }}
      >
        Retry
      </Button>
    </Container>
  );

  if (!camps.length) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        No camps available at this time
      </Typography>
      <Button 
        variant="contained" 
        onClick={() => fetchCamps()}
        sx={{ mt: 2 }}
      >
        Refresh
      </Button>
    </Container>
  );

  return (
    <Container maxWidth={false} sx={{ py: 4, width: '95%' }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Summer Camps 2025
      </Typography>
      <Typography variant="h5" component="h2" gutterBottom align="center" color="text.secondary" sx={{ mb: 4 }}>
        Weekly Schedule
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4, width: '100%' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Camp Type</TableCell>
              {weeks.map(week => (
                <TableCell 
                  key={week} 
                  align="center"
                  sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                >
                  Week {week}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {camps.find(c => c.weeks.find(w => w.week === week))?.weeks.find(w => w.week === week)?.dates || ''}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {campTypes.map(campType => {
              const camp = camps.find(c => c.title === campType);
              return (
                <TableRow 
                  key={campType} 
                  hover
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  <TableCell 
                    component="th" 
                    scope="row" 
                    sx={{ 
                      fontWeight: 'bold',
                      minWidth: 300,
                      pl: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {camp.imageUrl && (
                        <img
                          src={camp.imageUrl}
                          alt={camp.imageAlt}
                          style={{
                            width: '128px',
                            height: '128px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      )}
                      <Box>
                        {campType}
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => window.open(`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${camp.handle}`, '_blank')}
                          sx={{ 
                            mt: 1, 
                            display: 'block',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#1565c0',
                            }
                          }}
                        >
                          Course Info
                        </Button>
                      </Box>
                    </Box>
                  </TableCell>
                  {weeks.map(week => (
                    <TableCell 
                      key={`${campType}-${week}`} 
                      align="center"
                      sx={{ 
                        minWidth: 150,
                        p: 1
                      }}
                    >
                      {isWeekAvailable(camp, week) ? (
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            border: '1px solid rgba(76, 175, 80, 0.3)',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.02)',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }
                          }}
                          onClick={() => window.open(`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${camp.handle}`, '_blank')}
                        >
                          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {camp.title}
                            </Typography>
                            {getAvailabilityBadge(camp)}
                          </CardContent>
                        </Card>
                      ) : null}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Home;
